import React, { useState, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography, Button, IconButton } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { SectionTab } from './tab-button';
import { getImageSize } from '../utils/image';
import { t } from '../config';

const UploadPanelContent = observer(({ store }) => {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const processFiles = useCallback(
    async (files) => {
      const newImages = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        const url = URL.createObjectURL(file);
        try {
          const { width, height } = await getImageSize(url);
          newImages.push({
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            src: url,
            name: file.name,
            width,
            height,
          });
        } catch {
          URL.revokeObjectURL(url);
        }
      }
      setImages((prev) => [...prev, ...newImages]);
    },
    []
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files?.length) {
        processFiles(Array.from(e.dataTransfer.files));
      }
    },
    [processFiles]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback(
    (e) => {
      if (e.target.files?.length) {
        processFiles(Array.from(e.target.files));
      }
      e.target.value = '';
    },
    [processFiles]
  );

  const addImageToCanvas = useCallback(
    (img) => {
      const page = store.activePage;
      if (!page) return;

      const scale = Math.min(
        store.width / img.width,
        store.height / img.height,
        1
      );
      const w = img.width * scale;
      const h = img.height * scale;

      page.addElement({
        type: 'image',
        src: img.src,
        x: store.width / 2 - w / 2,
        y: store.height / 2 - h / 2,
        width: w,
        height: h,
      });
      store.history.addUndoState();
    },
    [store]
  );

  const removeImage = useCallback((id) => {
    setImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img) URL.revokeObjectURL(img.src);
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Drop zone */}
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: isDragging ? '#0d83cd' : '#ccc',
          borderRadius: '8px',
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragging ? 'rgba(13,131,205,0.06)' : 'transparent',
          transition: 'all 0.2s ease',
          mb: 2,
          '&:hover': {
            borderColor: '#0d83cd',
            backgroundColor: 'rgba(13,131,205,0.04)',
          },
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 36, color: '#999', mb: 1 }} />
        <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
          {t('sidePanel.uploadTip', 'Drop images here or click to upload')}
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
      </Box>

      {/* Uploaded images grid */}
      {images.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '8px',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {images.map((img) => (
            <Box
              key={img.id}
              sx={{
                position: 'relative',
                paddingTop: '100%',
                borderRadius: '6px',
                overflow: 'hidden',
                cursor: 'pointer',
                border: '1px solid #e0e0e0',
                '&:hover .upload-delete-btn': {
                  opacity: 1,
                },
                '&:hover': {
                  borderColor: '#0d83cd',
                },
              }}
              onClick={() => addImageToCanvas(img)}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                }}
              >
                <img
                  src={img.src}
                  alt={img.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
              <IconButton
                className="upload-delete-btn"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  removeImage(img.id);
                }}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  opacity: 0,
                  backgroundColor: 'rgba(255,255,255,0.85)',
                  transition: 'opacity 0.15s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,1)',
                  },
                }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
});

export const UploadSection = {
  name: 'upload',
  Tab: (props) => (
    <SectionTab name={t('sidePanel.upload', 'Upload')} {...props}>
      <CloudUploadIcon />
    </SectionTab>
  ),
  Panel: UploadPanelContent,
};
